import { scrubAddress, type IUserContext } from '@srm/screen-sharing/light';
import { APP_VERSION } from '@/configuration/appVersion';
import { recentSupportErrors } from '@utils/supportErrorLog';
import { SCREEN_SHARE_TITLE_PREFIX } from './ScreenShareContext';
import { getSupportDocument } from './supportDocument';

/** Браузер и система из User-Agent: «Chrome 141 · Windows». */
export function describeBrowser(userAgent: string): string | undefined {
    const browsers: [RegExp, string][] = [
        [/Edg(?:e|A|iOS)?\/(\d+)/, 'Edge'],
        [/(?:OPR|Opera)\/(\d+)/, 'Opera'],
        [/YaBrowser\/(\d+)/, 'Яндекс Браузер'],
        [/Firefox\/(\d+)/, 'Firefox'],
        [/(?:Chrome|CriOS)\/(\d+)/, 'Chrome'],
        // Chrome и Edge на iOS тоже пишут «Safari», но без «Version/» и выше по списку.
        [/Version\/(\d+)/, 'Safari'],
    ];
    const systems: [RegExp, string][] = [
        [/Windows/, 'Windows'],
        [/Android/, 'Android'],
        [/iPhone|iPad|iPod/, 'iOS'],
        [/Mac OS X|Macintosh/, 'macOS'],
        [/Linux/, 'Linux'],
    ];
    const browser = browsers.find(([pattern]) => pattern.test(userAgent));
    const version = browser ? userAgent.match(browser[0])?.[1] : undefined;
    const system = systems.find(([pattern]) => pattern.test(userAgent))?.[1];
    const name = browser ? `${browser[1]} ${version}` : undefined;
    return [name, system].filter(Boolean).join(' · ') || undefined;
}

/** Путь без `BASE_PATH`; секреты и пользовательский текст адреса маскирует общий `scrubAddress` пакета. */
function currentPath(): string {
    const { pathname, search } = window.location;
    const basePath = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '';
    const base = basePath && basePath !== '/' ? basePath.replace(/\/$/, '') : '';
    const path = base && pathname.startsWith(base) ? pathname.slice(base.length) || '/' : pathname;
    return scrubAddress(path + search);
}

/** Активная вкладка карточки документа — подпись выбранной вкладки в рабочей области. */
function activeTab(): string | undefined {
    const scope = document.querySelector('.main-content') ?? document;
    const tab = scope.querySelector('[role="tab"][aria-selected="true"]');
    return tab?.textContent?.replace(/\s+/g, ' ').trim() || undefined;
}

/** Снимок контекста пользователя для сотрудника поддержки (IMPLEMENTATION.md §11); ошибки — с момента `errorsSince`. */
export function buildUserContext(errorsSince?: number): IUserContext {
    const title = document.title.startsWith(SCREEN_SHARE_TITLE_PREFIX)
        ? document.title.slice(SCREEN_SHARE_TITLE_PREFIX.length)
        : document.title;
    const errors = recentSupportErrors(errorsSince);
    const currentDocument = getSupportDocument();
    const tab = currentDocument ? activeTab() : undefined;
    const browser = describeBrowser(navigator.userAgent);
    return {
        path: currentPath(),
        ...(title ? { title } : {}),
        ...(currentDocument
            ? {
                  documentId: currentDocument.id,
                  ...(currentDocument.title ? { documentTitle: currentDocument.title } : {}),
                  ...(tab ? { tab } : {}),
              }
            : {}),
        frontVersion: APP_VERSION,
        ...(browser ? { browser } : {}),
        window: {
            width: window.innerWidth,
            height: window.innerHeight,
            dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
        },
        hidden: document.hidden || !document.hasFocus(),
        ...(errors.length ? { errors } : {}),
    };
}
